import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { stateName } from "@/lib/geo";

export default function StatePage() {
  const { stateSlug } = useParams();
  const [cities, setCities] = useState(null);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data.filter((c) => c.state_slug === stateSlug)));
  }, [stateSlug]);

  const name = stateName(stateSlug);

  useEffect(() => {
    if (!cities) return;
    setPageMeta(
      `Run Clubs in ${name} — City-by-City Directory | Ninja Run Club`,
      `Find verified run clubs across ${name}: ${cities.map((c) => c.name).join(", ") || "browse the national directory"}. Meeting days, paces, and how to join — human-verified.`
    );
    return setJsonLd("state-jsonld", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `Run club cities in ${name}`,
      numberOfItems: cities.length,
      itemListElement: cities.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `Run clubs in ${c.name}`,
        item: `${SITE_BASE}/run-clubs/${c.state_slug}/${c.slug}`,
      })),
    });
  }, [cities, name]);

  if (cities && cities.length === 0) {
    setPageMeta(`Page Not Found | Ninja Run Club`, "", { noindex: true });
    return (
      <div className="min-h-[60vh] bg-night text-white flex flex-col items-center justify-center gap-5 px-5 text-center" data-testid="state-not-found">
        <p className="font-display text-4xl">NO CITY HUBS IN {name.toUpperCase()} YET.</p>
        <Link to="/clubs" className="btn-invert bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white">
          SEARCH THE FULL DIRECTORY
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-night text-white min-h-screen" data-testid="state-page">
      <div className="px-5 lg:px-10 pt-10 pb-20 max-w-6xl mx-auto">
        <Breadcrumbs light items={[["Home", "/"], ["Run Clubs", "/clubs"], [name, `/run-clubs/${stateSlug}`]]} />
        <Reveal>
          <p className="font-mono-label text-white/50 mt-10">STATE INDEX</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[0.95]">RUN CLUBS IN {name.toUpperCase()}.</h1>
          <p className="text-white/60 mt-5 max-w-2xl text-base">
            Every {name} city hub in the Ninja Run Club directory. Each hub lists verified clubs with meeting days, pace culture, routes worth running, and what to expect at your first visit.
          </p>
        </Reveal>
        <div className="mt-12 border-t border-white/10">
          {(cities || []).map((c) => (
            <Link
              key={c.slug}
              to={`/run-clubs/${c.state_slug}/${c.slug}`}
              data-testid={`state-city-${c.slug}`}
              className="group flex items-center justify-between border-b border-white/10 py-6 px-2 hover:bg-white hover:text-ink transition-colors duration-300"
            >
              <div>
                <span className="font-display text-2xl lg:text-3xl">{c.name.toUpperCase()}</span>
                <p className="font-mono-label text-white/40 group-hover:text-ink/50 mt-1">{c.tagline}</p>
              </div>
              <ArrowUpRight size={22} className="opacity-40 group-hover:opacity-100 transition-opacity duration-200" />
            </Link>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/clubs" className="font-mono-label border border-white/20 px-4 py-2.5 hover:bg-white hover:text-ink transition-colors" data-testid="state-all-clubs-link">
            FULL NATIONAL DIRECTORY →
          </Link>
          <Link to="/ranked" className="font-mono-label border border-white/20 px-4 py-2.5 hover:bg-white hover:text-ink transition-colors">
            THE 2026 RANKINGS →
          </Link>
        </div>
      </div>
    </div>
  );
}
